export type CountrySelection = { mode: 'all' } | { mode: 'subset'; regions: string[] };

/** Sélection complète, indexée par code pays ISO-3166-1 alpha-2. */
export type GeoSelection = Record<string, CountrySelection>;

export interface CountryMeta {
  code: string;
  name: string;
  unitCount: number;
}

export interface RegionMeta {
  code: string;
  name: string;
  unitCount: number;
}

/** Catalogue des régions par pays, alimenté paresseusement au survol/ouverture. */
export type RegionIndex = Record<string, RegionMeta[] | undefined>;

export type TriState = 'none' | 'partial' | 'all';

/** Payload canonique envoyé au backend. `regions: null` = pays entier. */
export interface GeoPayloadEntry {
  country: string;
  regions: string[] | null;
}

export function countryState(sel: GeoSelection, cc: string): TriState {
  const entry = sel[cc];
  if (!entry) return 'none';
  if (entry.mode === 'all') return 'all';
  return entry.regions.length > 0 ? 'partial' : 'none';
}

function withoutCountry(sel: GeoSelection, cc: string): GeoSelection {
  const next = { ...sel };
  delete next[cc];
  return next;
}

/** Trie une liste de codes selon l'ordre du catalogue, pour que l'affichage
 *  et le payload soient déterministes quel que soit l'ordre des clics. */
function orderByCatalog(codes: Iterable<string>, catalog: RegionMeta[]): string[] {
  const rank = new Map(catalog.map((r, i) => [r.code, i]));
  return [...codes].sort((a, b) => (rank.get(a) ?? Infinity) - (rank.get(b) ?? Infinity));
}

export function toggleCountry(sel: GeoSelection, cc: string): GeoSelection {
  // La case du pays est binaire : cocher = pays entier, décocher = rien.
  // Un état partiel se comporte comme « non coché » et bascule donc en all.
  if (countryState(sel, cc) === 'all') return withoutCountry(sel, cc);
  return { ...sel, [cc]: { mode: 'all' } };
}

export function toggleRegion(
  sel: GeoSelection,
  cc: string,
  regionCode: string,
  catalog: RegionMeta[],
): GeoSelection {
  const entry = sel[cc];

  if (!entry) {
    return { ...sel, [cc]: { mode: 'subset', regions: [regionCode] } };
  }

  if (entry.mode === 'all') {
    // MATÉRIALISATION : « tout sauf X » n'est pas représentable dans le modèle,
    // il faut donc développer le complément explicite. Sans catalogue chargé
    // c'est impossible : on refuse plutôt que de produire une sélection fausse.
    if (catalog.length === 0) return sel;
    const remaining = catalog.map((r) => r.code).filter((code) => code !== regionCode);
    if (remaining.length === 0) return withoutCountry(sel, cc);
    return { ...sel, [cc]: { mode: 'subset', regions: remaining } };
  }

  const set = new Set(entry.regions);
  if (set.has(regionCode)) {
    set.delete(regionCode);
    if (set.size === 0) return withoutCountry(sel, cc);
    return { ...sel, [cc]: { mode: 'subset', regions: orderByCatalog(set, catalog) } };
  }

  set.add(regionCode);
  // Le sous-ensemble complet se replie en mode all : le payload reste compact
  // et le backend n'a pas à comparer une liste de 96 codes à son catalogue.
  if (catalog.length > 0 && set.size === catalog.length) {
    return { ...sel, [cc]: { mode: 'all' } };
  }
  return { ...sel, [cc]: { mode: 'subset', regions: orderByCatalog(set, catalog) } };
}

export function selectRange(
  sel: GeoSelection,
  cc: string,
  fromIndex: number,
  toIndex: number,
  catalog: RegionMeta[],
): GeoSelection {
  if (catalog.length === 0) return sel;
  // Déjà tout coché : le shift-clic n'a rien à ajouter.
  if (countryState(sel, cc) === 'all') return sel;

  const lo = Math.max(0, Math.min(fromIndex, toIndex));
  const hi = Math.min(catalog.length - 1, Math.max(fromIndex, toIndex));
  if (hi < lo) return sel;

  const entry = sel[cc];
  const set = new Set(entry && entry.mode === 'subset' ? entry.regions : []);
  for (let i = lo; i <= hi; i += 1) set.add(catalog[i]!.code);

  if (set.size === catalog.length) return { ...sel, [cc]: { mode: 'all' } };
  return { ...sel, [cc]: { mode: 'subset', regions: orderByCatalog(set, catalog) } };
}

export function totals(
  sel: GeoSelection,
  countries: CountryMeta[],
  regionIndex: RegionIndex,
): { units: number; countries: number } {
  const byCode = new Map(countries.map((c) => [c.code, c]));
  let units = 0;
  let selectedCountries = 0;

  for (const [cc, entry] of Object.entries(sel)) {
    const meta = byCode.get(cc);
    if (!meta) continue;

    if (entry.mode === 'all') {
      // unitCount du pays fait autorité : c'est le seul chiffre disponible
      // tant que les régions n'ont pas été téléchargées, et il reste
      // le plus fiable même après (le backend l'a calculé sur ses zones).
      units += meta.unitCount;
      selectedCountries += 1;
      continue;
    }

    if (entry.regions.length === 0) continue;
    const catalog = regionIndex[cc] ?? [];
    const byRegion = new Map(catalog.map((r) => [r.code, r.unitCount]));
    units += entry.regions.reduce((acc, code) => acc + (byRegion.get(code) ?? 0), 0);
    selectedCountries += 1;
  }

  return { units, countries: selectedCountries };
}

export function toGeoPayload(sel: GeoSelection): GeoPayloadEntry[] {
  return Object.entries(sel)
    .filter(([, entry]) => entry.mode === 'all' || entry.regions.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([country, entry]) => ({
      country,
      regions: entry.mode === 'all' ? null : [...entry.regions].sort((a, b) => a.localeCompare(b)),
    }));
}

/** Adaptation au corps réel de POST /jobs, qui reçoit deux listes plates :
 *  les pays pris en entier d'un côté, les codes de régions de l'autre.
 *  Un pays en sous-ensemble n'apparaît PAS dans country_codes, sinon le
 *  backend l'expanserait en entier et le sous-ensemble serait ignoré. */
export function toJobGeoFields(sel: GeoSelection): {
  country_codes: string[];
  admin1_codes: string[];
} {
  const payload = toGeoPayload(sel);
  return {
    country_codes: payload.filter((e) => e.regions === null).map((e) => e.country),
    admin1_codes: payload.flatMap((e) => e.regions ?? []),
  };
}
